import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  X, Radar, Navigation, Heart, Trash2, Pencil, Users, Check, Palette, RotateCcw, ShieldCheck,
} from "lucide-react";
import { openDirections } from "../lib/directions";
import IdentityBadge from "./IdentityBadge";
import { SHAPES, SHAPE_LABELS, COLORS, identityFor } from "../lib/mapIdentity";

/**
 * Bottom-sheet modal for managing a single contact (View / Navigate / Rename /
 * Relationship / Remove). Opens when a marker is double-tapped on the map
 * or from anywhere in the app.
 */
export default function ContactSheet({ contact, onClose, onChanged }) {
  const navigate = useNavigate();
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(contact?.name || "");
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [showRelation, setShowRelation] = useState(false);

  if (!contact) return null;

  const isFamily = ["family", "child", "parent"].includes(contact.relation);

  const rename = async () => {
    if (!name.trim() || name.trim() === contact.name) { setEditingName(false); return; }
    setSaving(true);
    try {
      await api.patch(`/contacts/${contact.user_id}`, { name: name.trim() });
      toast.success("Name updated");
      setEditingName(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const setRelation = async (newRel) => {
    setSaving(true);
    try {
      if (newRel === "family" || newRel === "friend") {
        await api.post(`/contacts/${contact.user_id}/family`, { is_family: newRel === "family" });
      } else {
        // child/parent flows still route through /family; unchanged.
        await api.post(`/contacts/${contact.user_id}/family`, { is_family: true });
      }
      toast.success("Relationship updated");
      setShowRelation(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api.delete(`/contacts/${contact.user_id}`);
      toast.message(`${contact.name} removed`);
      onClose?.();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div
        data-testid="contact-sheet"
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-zinc-950 border-t sm:border border-white/10 rounded-t-3xl sm:rounded-3xl p-6"
      >
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="size-14 rounded-full bg-zinc-800 overflow-hidden grid place-items-center font-display font-black text-xl shrink-0">
            {contact.picture ? <img src={contact.picture} alt="" className="w-full h-full object-cover"/> : (contact.name || "?").slice(0,1)}
          </div>
          <div className="flex-1 min-w-0">
            {editingName ? (
              <div className="flex items-center gap-2">
                <input
                  data-testid="rename-input"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, 40))}
                  onKeyDown={(e) => { if (e.key === "Enter") rename(); if (e.key === "Escape") { setEditingName(false); setName(contact.name); } }}
                  className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#007AFF]"
                />
                <button data-testid="rename-save" onClick={rename} className="size-8 rounded-lg bg-[#34C759] text-white grid place-items-center" disabled={saving}>
                  <Check size={14}/>
                </button>
              </div>
            ) : (
              <>
                <div className="font-display font-black text-2xl tracking-tighter truncate">{contact.name}</div>
                <div className="text-xs text-zinc-500 truncate capitalize">
                  {contact.relation}
                  {contact.distance_miles != null && (
                    <span className="ml-1.5 font-mono">
                      · {contact.distance_miles < 0.19
                        ? `${Math.round(contact.distance_miles * 5280)} ft`
                        : `${contact.distance_miles.toFixed(2)} mi`}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <button
            data-testid="close-sheet"
            onClick={onClose}
            className="size-9 rounded-full grid place-items-center text-zinc-500 hover:text-zinc-100"
          >
            <X size={16}/>
          </button>
        </div>

        {/* Primary actions row */}
        <div className="mt-5 grid grid-cols-5 gap-2">
          <ActionBtn
            testid="sheet-find"
            icon={Radar}
            label="Find"
            color="#007AFF"
            onClick={() => { onClose?.(); navigate(`/find/${contact.user_id}`); }}
          />
          <ActionBtn
            testid="sheet-navigate"
            icon={Navigation}
            label="Navigate"
            color="#34C759"
            disabled={!contact.latest}
            onClick={() => {
              if (!contact.latest) return;
              openDirections({ lat: contact.latest.lat, lng: contact.latest.lng, label: contact.name, mode: "driving" });
            }}
          />
          <ActionBtn
            testid="sheet-rename"
            icon={Pencil}
            label="Rename"
            color="#a1a1aa"
            onClick={() => setEditingName(true)}
          />
          <ActionBtn
            testid="sheet-relation"
            icon={isFamily ? Heart : Users}
            label={isFamily ? "Family" : "Friend"}
            color={isFamily ? "#FF2D55" : "#a1a1aa"}
            onClick={() => setShowRelation((v) => !v)}
          />
          <ActionBtn
            testid="sheet-geofences"
            icon={ShieldCheck}
            label="Zones"
            color="#FFCC00"
            onClick={() => { onClose?.(); navigate(`/geofence/${contact.user_id}?return=dashboard`); }}
          />
        </div>

        {showRelation && (
          <div className="mt-3 p-3 rounded-2xl border border-white/10 bg-white/[0.03] space-y-1.5">
            <div className="label-eyebrow px-1 mb-1">Relationship</div>
            <RelOpt selected={contact.relation === "friend"} onClick={() => setRelation("friend")} icon={Users} label="Friend"/>
            <RelOpt selected={contact.relation === "family"} onClick={() => setRelation("family")} icon={Heart} label="Family (favorite)"/>
            {["child", "parent"].includes(contact.relation) && (
              <div className="px-3 py-2 text-xs text-zinc-500">Relationship: <span className="text-zinc-300 capitalize font-semibold">{contact.relation}</span> (managed via device link)</div>
            )}
          </div>
        )}

        {/* Map Identity — shape + color + display name */}
        <MapIdentityEditor contact={contact} onChanged={onChanged} />

        {/* Danger zone */}
        <div className="mt-5 pt-5 border-t border-white/5">
          {!confirmRemove ? (
            <button
              data-testid="sheet-remove"
              onClick={() => setConfirmRemove(true)}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full border border-white/10 text-[#FF3B30] hover:bg-[#FF3B30]/8 font-bold text-sm"
            >
              <Trash2 size={14}/> Remove contact
            </button>
          ) : (
            <div>
              <div className="text-sm text-zinc-300">
                Are you sure you want to remove <span className="font-semibold text-zinc-100">{contact.name}</span>?
              </div>
              <div className="text-xs text-zinc-500 mt-1">You'll disappear from each other's maps. You can reconnect later by invite.</div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => setConfirmRemove(false)}
                  className="flex-1 py-2.5 rounded-full border border-white/10 font-bold text-sm"
                >
                  Cancel
                </button>
                <button
                  data-testid="sheet-confirm-remove"
                  onClick={remove}
                  disabled={saving}
                  className="flex-1 py-2.5 rounded-full bg-[#FF3B30] text-white font-bold text-sm disabled:opacity-50"
                >
                  Remove contact
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ testid, icon: Icon, label, color, onClick, disabled }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center gap-1.5 p-3 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] disabled:opacity-40 disabled:cursor-not-allowed"
    >
      <div className="size-9 rounded-full grid place-items-center" style={{ background: `${color}20` }}>
        <Icon size={17} style={{ color }}/>
      </div>
      <span className="text-[11px] font-bold uppercase tracking-wide">{label}</span>
    </button>
  );
}

function RelOpt({ selected, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left ${selected ? "bg-[#007AFF]/15 text-[#007AFF]" : "hover:bg-white/5"}`}
    >
      <Icon size={14}/> <span className="font-semibold">{label}</span>
      {selected && <span className="ml-auto text-[10px] font-black uppercase">On</span>}
    </button>
  );
}


/**
 * Map Identity editor — shape + color + display name, with live preview
 * and one-tap Reset. All changes save immediately via PATCH /contacts/{id}.
 */
function MapIdentityEditor({ contact, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState(contact.display_name || contact.name || "");
  const { shape, color } = identityFor(contact);
  const preview = { display_name: displayName, map_shape: shape, map_color: color, is_family: contact.is_family };

  const patch = async (body) => {
    setSaving(true);
    try {
      await api.patch(`/contacts/${contact.user_id}`, body);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };
  const resetIdentity = async () => {
    setSaving(true);
    try {
      await api.post(`/contacts/${contact.user_id}/reset-identity`);
      toast.success("Identity reset");
      onChanged?.();
    } catch { toast.error("Reset failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="map-identity-section">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 p-3.5"
        data-testid="map-identity-toggle"
      >
        <IdentityBadge contact={preview} size={28} />
        <div className="flex-1 text-left">
          <div className="label-eyebrow flex items-center gap-1.5"><Palette size={11}/> Map Identity</div>
          <div className="text-xs text-zinc-500 mt-0.5 truncate">{displayName || "no display name"} · {SHAPE_LABELS[shape]} · {color}</div>
        </div>
        <span className="text-zinc-500 text-xs">{expanded ? "▴" : "▾"}</span>
      </button>

      {expanded && (
        <div className="p-3.5 pt-0 space-y-3">
          {/* Display name */}
          <div>
            <div className="label-eyebrow mb-1.5">Display name</div>
            <div className="flex gap-2">
              <input
                data-testid="identity-name-input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={contact.name}
                className="flex-1 bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007AFF]"
              />
              <button
                data-testid="identity-name-save"
                disabled={saving || !displayName.trim() || displayName === (contact.display_name || contact.name)}
                onClick={() => patch({ display_name: displayName.trim() })}
                className="px-3 rounded-lg bg-[#34C759] text-white font-bold text-sm disabled:opacity-40"
              ><Check size={14}/></button>
            </div>
          </div>

          {/* Shape picker */}
          <div>
            <div className="label-eyebrow mb-1.5">Shape</div>
            <div className="grid grid-cols-8 gap-1.5">
              {SHAPES.map((s) => (
                <button
                  key={s}
                  data-testid={`identity-shape-${s}`}
                  disabled={saving}
                  onClick={() => patch({ map_shape: s })}
                  className={`aspect-square rounded-lg grid place-items-center transition-colors ${
                    shape === s ? "bg-[#007AFF]/20 border border-[#007AFF]" : "border border-white/10 hover:bg-white/5"
                  }`}
                  title={SHAPE_LABELS[s]}
                >
                  <IdentityBadge contact={{ map_shape: s, map_color: color }} size={22} />
                </button>
              ))}
            </div>
          </div>

          {/* Color picker */}
          <div>
            <div className="label-eyebrow mb-1.5">Color</div>
            <div className="grid grid-cols-9 gap-1.5">
              {COLORS.map((c) => (
                <button
                  key={c.key}
                  data-testid={`identity-color-${c.key}`}
                  disabled={saving}
                  onClick={() => patch({ map_color: c.key })}
                  className={`aspect-square rounded-lg border-2 transition-all ${
                    color === c.key ? "border-white scale-110" : "border-white/10 hover:border-white/40"
                  }`}
                  style={{ background: c.hex }}
                  title={c.label}
                />
              ))}
            </div>
          </div>

          <button
            data-testid="identity-reset"
            onClick={resetIdentity}
            disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg border border-white/10 text-xs font-bold uppercase tracking-wider text-zinc-400 hover:bg-white/5"
          >
            <RotateCcw size={12}/> Reset to default
          </button>
        </div>
      )}
    </div>
  );
}
